// PLAN-cockpit-v2 Phase 0-C: 共通 MetaBlock
// 5W1H + source.* + domain (declared/curated/inferred、provenance + confidence visualization)

interface DomainBadge {
  domain: string
  provenance: 'declared' | 'curated' | 'inferred'
  polarity?: 'include' | 'exclude'
  confidence?: number | null
  source?: string | null
  reason?: string | null
}

interface MetaBlockProps {
  source?: {
    tool?: string | null
    medium?: string | null
    project?: string | null
  } | null
  source_actor?: string | null
  source_context?: string | null
  origin?: string | null
  subjectivity?: string | null
  visibility?: string | null
  lifecycle_stage?: string | null
  declared_domains?: string[]
  curated?: DomainBadge[]
  inferred?: DomainBadge[]
  created_at?: string | null
  updated_at?: string | null
  // PVW Core projection (PLAN-pvw-dsl-adoption, 2026-05-13)
  migration_status?: 'pending' | 'migrated' | 'migrated_with_warnings' | 'quarantined' | 'rejected' | null
  text_schema_status?: 'primary_axis' | 'context_only' | 'weak_context' | 'invalid_text_schema' | 'not_article' | null
  has_decision?: boolean | number | null
  x_status?: 'present' | 'all_rejected' | 'not_applicable' | null
  decision_size_class?: 'normal' | 'large' | null
}

const DOMAIN_BG: Record<string, string> = {
  finance: 'bg-domain-finance',
  health: 'bg-domain-health',
  mind: 'bg-domain-mind',
  relation: 'bg-domain-relation',
  'science-tech': 'bg-domain-science-tech',
  general: 'bg-domain-general',
}

const DOMAIN_TEXT: Record<string, string> = {
  finance: 'text-domain-finance',
  health: 'text-domain-health',
  mind: 'text-domain-mind',
  relation: 'text-domain-relation',
  'science-tech': 'text-domain-science-tech',
  general: 'text-domain-general',
}

const DOMAIN_BORDER: Record<string, string> = {
  finance: 'border-domain-finance',
  health: 'border-domain-health',
  mind: 'border-domain-mind',
  relation: 'border-domain-relation',
  'science-tech': 'border-domain-science-tech',
  general: 'border-domain-general',
}

function declaredBadge(domain: string) {
  const bg = DOMAIN_BG[domain] ?? 'bg-domain-general'
  return (
    <span
      key={`declared-${domain}`}
      className={`text-[11px] px-2 py-0.5 rounded-sm text-bg-surface ${bg}`}
      title={`declared: ${domain}`}
    >
      {domain}
    </span>
  )
}

function curatedBadge(b: DomainBadge) {
  const border = DOMAIN_BORDER[b.domain] ?? 'border-domain-general'
  const text = DOMAIN_TEXT[b.domain] ?? 'text-domain-general'
  const excl = b.polarity === 'exclude'
  return (
    <span
      key={`curated-${b.domain}-${b.polarity}`}
      className={`text-[11px] px-2 py-0.5 rounded-sm border ${border} ${text} ${excl ? 'line-through opacity-70' : ''}`}
      title={`curated ${b.polarity}: ${b.domain}${b.reason ? ` (${b.reason})` : ''}`}
    >
      {b.domain}
    </span>
  )
}

function inferredBadge(b: DomainBadge) {
  const text = DOMAIN_TEXT[b.domain] ?? 'text-domain-general'
  const bg = DOMAIN_BG[b.domain] ?? 'bg-domain-general'
  const conf = b.confidence ?? 0
  // 薄塗り badge + confidence visualization (bar)
  return (
    <span
      key={`inferred-${b.domain}`}
      className={`text-[11px] px-2 py-0.5 rounded-sm border border-border-subtle ${text} flex items-center gap-1.5`}
      title={`inferred: ${b.domain} (confidence ${conf.toFixed(2)})${b.reason ? `\n${b.reason}` : ''}`}
    >
      <span>{b.domain}</span>
      <span className="inline-block w-6 h-1 bg-border-subtle rounded overflow-hidden" aria-hidden>
        <span
          className={`block h-full ${bg}`}
          style={{ width: `${Math.max(0, Math.min(1, conf)) * 100}%` }}
        />
      </span>
    </span>
  )
}

export function MetaBlock(props: MetaBlockProps) {
  const {
    source,
    source_actor,
    source_context,
    origin,
    subjectivity,
    visibility,
    lifecycle_stage,
    declared_domains = [],
    curated = [],
    inferred = [],
    created_at,
    updated_at,
    migration_status,
    text_schema_status,
    has_decision,
    x_status,
    decision_size_class,
  } = props

  const sourceTool = source?.tool ?? null
  const sourceMedium = source?.medium ?? null
  const sourceProject = source?.project ?? null

  const hasAnyDomain = declared_domains.length > 0 || curated.length > 0 || inferred.length > 0

  return (
    <dl className="text-xs text-text-muted space-y-2">
      {origin && (
        <Row label="Origin">
          <span className="text-text-base">{origin}</span>
          {subjectivity && <span className="text-text-subtle"> · {subjectivity}</span>}
        </Row>
      )}
      {visibility && <Row label="Visibility"><span className="text-text-base">{visibility}</span></Row>}
      {lifecycle_stage && <Row label="Lifecycle"><span className="text-text-base">{lifecycle_stage}</span></Row>}
      {(sourceProject || sourceTool || sourceMedium) && (
        <Row label="Source">
          <span className="text-text-base">
            {sourceProject || '—'}
            {sourceTool && <span className="text-text-subtle"> via {sourceTool}</span>}
            {sourceMedium && <span className="text-text-subtle"> · {sourceMedium}</span>}
          </span>
        </Row>
      )}
      {source_actor && <Row label="Actor"><span className="text-text-base">{source_actor}</span></Row>}
      {source_context && <Row label="Context"><span className="text-text-base">{source_context}</span></Row>}
      {hasAnyDomain && (
        <Row label="Domain">
          <div className="flex flex-wrap gap-1.5">
            {declared_domains.map((d) => declaredBadge(d))}
            {curated.map((b) => curatedBadge(b))}
            {inferred.map((b) => inferredBadge(b))}
          </div>
        </Row>
      )}
      {created_at && (
        <Row label="Created"><span className="text-text-base font-mono text-xs">{formatDate(created_at)}</span></Row>
      )}
      {updated_at && updated_at !== created_at && (
        <Row label="Updated"><span className="text-text-base font-mono text-xs">{formatDate(updated_at)}</span></Row>
      )}
      {migration_status && migration_status !== 'migrated' && (
        <Row label="PVW">
          <span className={migrationStatusClass(migration_status)}>{migration_status}</span>
          {text_schema_status && <span className="text-text-subtle"> · {text_schema_status}</span>}
        </Row>
      )}
      {(has_decision === true || has_decision === 1) && (
        <Row label="Decision">
          <span className="text-text-base">
            {x_status ?? 'present'}
            {decision_size_class === 'large' && <span className="text-amber-500"> · large</span>}
          </span>
        </Row>
      )}
    </dl>
  )
}

function migrationStatusClass(status: string): string {
  switch (status) {
    case 'rejected':
    case 'quarantined':
      return 'text-red-500 font-medium'
    case 'migrated_with_warnings':
      return 'text-amber-500 font-medium'
    default:
      return 'text-text-base'
  }
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-20 flex-shrink-0 text-text-subtle">{label}</dt>
      <dd className="flex-1 min-w-0">{children}</dd>
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 16).replace('T', ' ')
  } catch {
    return iso
  }
}
